import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogTitle,
	DialogTrigger,
	Label,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { DialogActions } from "molecules/dialog/DialogActions";
import { Input } from "molecules/input/Input";
import { Select } from "molecules/select/Select";
import { type ComponentProps, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useCreateCompanyMutation } from "../../common/redux/api/exopenApi";
import { SCOPE_SWITCH_STATE } from "src/routes/scopeChange.js";

import { companyTypes } from "./companyTypes.ts";

export const AddCompany = ({
	children,
	onClose,
	onCreated,
	open,
}: {
	children?: ComponentProps<typeof DialogTrigger>["children"];
	open?: boolean;
	onClose?: () => void;
	/** Called once the domain is created, before navigating to it. */
	onCreated: () => void;
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [companyName, setCompanyName] = useState<string>("");
	const [companyType, setCompanyType] = useState<string>("");

	const [createCompany, { isLoading: isCreatingCompany }] =
		useCreateCompanyMutation();

	const createNewCompany = async () => {
		const result = await createCompany({
			name: companyName,
			type: companyType,
		});

		if ("error" in result) {
			showErrorNotification({
				message: `${t("The company")} ${companyName} ${t("was not saved")}`,
			});
		} else {
			onCreated();
			navigate(
				{ pathname: `/company-domains/${result.data.id}` },
				{ state: SCOPE_SWITCH_STATE },
			);
		}
	};

	const typeLabelId = useId();

	return (
		<Dialog open={open} onClose={onClose}>
			{children && <DialogTrigger asChild>{children}</DialogTrigger>}
			<DialogContent>
				<DialogTitle>{t("New Company Domain")}</DialogTitle>
				<DialogBody>
					<Label id={typeLabelId}>{t("Type")}</Label>
					<Select
						aria-labelledby={typeLabelId}
						placeholder={t("Select type")}
						options={companyTypes}
						value={companyType}
						onChange={(value) => {
							if (value !== null) {
								setCompanyType(value);
							}
						}}
						className="mb-4"
					/>
					<Label htmlFor="create-company-name">Namn</Label>
					<Input
						id="create-company-name"
						onChange={(event) => {
							setCompanyName(event.target.value);
						}}
						value={companyName}
					/>
				</DialogBody>
				<DialogActions withCancelButton>
					<Button
						disabled={companyName === "" || companyType === ""}
						variant="primary"
						onClick={createNewCompany}
						isLoading={isCreatingCompany}
					>
						{t("Create Company Domain")}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
};
